import React, {
    Fragment, useContext, useMemo
} from 'react';
import ModalCtrl from 'sg-modal-ctrl';
import { TranslatorContext } from '@jutro/locale';
import SgSptConstants from 'sg-spt-constants';
import { SgSptTripUtils } from 'sg-spt-travel-util';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import messages from './SgSptExcessesModal.messages';

function SgSptExcessesModal(props) {
    const {
        isOpen,
        onResolve,
        modelVM,
    } = props;

    const {
        ESSENTIAL,
        PLUS
    } = SgSptConstants.COVER_LEVELS;
    const showEssentialCoverLevel = SgSptTripUtils.isEssentialCoverLevelAvailable(modelVM);
    const selectedTripIndex = SgSptTripUtils.getSelectedTripIndex(modelVM);
    const selectedTripVM = modelVM.lobData.privateTravel.coverables.sptTrips.children[selectedTripIndex];
    const excessValue = selectedTripVM.excessValueExt.value.code.replace('_excess', '');
    const showAllCoverLevels = selectedTripVM.allCoverLevelsAvailable.value;
    const hasCruiseTypeOfTrip = _.some(selectedTripVM.value.sptTripActivityTypes, (el) => el.tripType === SgSptConstants.TYPE_OF_TRIPS.CRUISE);
    const isSelectedTripSingle = SgSptTripUtils.isSelectedTripSingle(modelVM);

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);

    const isMobile = breakpoint === 'phoneWide' || breakpoint === 'phone';

    const tableData = useMemo(() => {
        const data = [];

        if (selectedTripVM.value.isCancelCovNeeded || !isSelectedTripSingle) {
            data.push({
                id: 'cancellation',
                order: 0,
                name: translator(messages.CancellationName),
                [ESSENTIAL]: translator(messages.CancellationEssentialAmount),
                [PLUS]: translator(messages.CancellationStandardPlusAmount, {
                    excessInput: excessValue
                }),
            });
        }

        data.push({
            id: 'medicalExpenses',
            order: 10,
            name: translator(messages.MedicalExpensesName),
            [ESSENTIAL]: translator(messages.MedicalExpensesEssentialValue),
            [PLUS]: translator(messages.MedicalExpensesStandardPlusValue, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'lostStolenDamagedBaggage',
            order: 20,
            name: translator(messages.LostStolenBaggageName),
            [ESSENTIAL]: translator(messages.LostStolenBaggageEssential),
            [PLUS]: translator(messages.LostStolenBaggageStandardPlus, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'hospitalBenefit',
            order: 90,
            name: translator(messages.HospitalBenefitName),
            [ESSENTIAL]: translator(messages.HospitalBenefitEssential),
            [PLUS]: translator(messages.HospitalBenefitStandardPlus),
        });

        data.push({
            id: 'delayedBaggage',
            order: 80,
            name: translator(messages.DelayedBaggageName),
            [ESSENTIAL]: translator(messages.DelayedBaggageEssential),
            [PLUS]: translator(messages.DelayedBaggageStandardPlus),
        });

        data.push({
            id: 'delayedFlightAssistance',
            order: 40,
            name: translator(messages.DelayedFlightAssistanceName),
            [ESSENTIAL]: translator(messages.DelayedFlightAssistanceEssential),
            [PLUS]: translator(messages.DelayedFlightAssistanceStandardPlus),
        });

        data.push({
            id: 'missedDeparture',
            order: 60,
            name: translator(messages.MissedDepartureName),
            [ESSENTIAL]: translator(messages.MissedDepartureEssential),
            [PLUS]: translator(messages.MissedDepartureStandardPlus),
        });

        data.push({
            id: 'delayedDeparture',
            order: 30,
            name: translator(messages.DelayedDepartureName),
            [ESSENTIAL]: translator(messages.DelayedDepartureEssential),
            [PLUS]: translator(messages.DelayedDepartureStandardPlus),
        });

        data.push({
            id: 'abandonment',
            order: 70,
            name: translator(messages.AbandonmentName),
            [ESSENTIAL]: translator(messages.AbandonmentEssential),
            [PLUS]: translator(messages.AbandonmentStandardPlus, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'personalAccident',
            order: 100,
            name: translator(messages.PersonalAccidentName),
            [ESSENTIAL]: translator(messages.PersonalAccidentEssential),
            [PLUS]: translator(messages.PersonalAccidentStandardPlus),
        });

        data.push({
            id: 'criminalInjury',
            order: 110,
            name: translator(messages.CriminalInjuryName),
            [ESSENTIAL]: translator(messages.CriminalInjuryEssential),
            [PLUS]: translator(messages.CriminalInjuryStandardPlus),
        });

        data.push({
            id: 'substituteAccommodation',
            order: 120,
            name: translator(messages.SubstituteAccommodationName),
            [ESSENTIAL]: translator(messages.SubstituteAccommodationEssential),
            [PLUS]: translator(messages.SubstituteAccommodationStandardPlus, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'enforcedStay',
            order: 50,
            name: translator(messages.EnforcedStayName),
            [ESSENTIAL]: translator(messages.EnforcedStayEssential),
            [PLUS]: translator(messages.EnforcedStayStandardPlus, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'lossOfPassport',
            order: 130,
            name: translator(messages.PassportLossName),
            [ESSENTIAL]: translator(messages.PassportLossEssential),
            [PLUS]: translator(messages.PassportLossStandardPlus),
        });

        data.push({
            id: 'personalMoney',
            order: 140,
            name: translator(messages.PersonalMoneyName),
            [ESSENTIAL]: translator(messages.PersonalMoneyEssential),
            [PLUS]: translator(messages.PersonalMoneyStandardPlus, {
                excessInput: excessValue
            }),
        });

        data.push({
            id: 'petCare',
            order: 160,
            name: translator(messages.PetCareName),
            [ESSENTIAL]: translator(messages.PetCareEssential),
            [PLUS]: translator(messages.PetCareStandardPlus),
        });

        data.push({
            id: 'hijackAndMugging',
            order: 170,
            name: translator(messages.HijackMuggingName),
            [ESSENTIAL]: translator(messages.HijackMuggingEssential),
            [PLUS]: translator(messages.HijackMuggingStandardPlus),
        });

        data.push({
            id: 'airRage',
            order: 180,
            name: translator(messages.AirRageName),
            [ESSENTIAL]: translator(messages.AirRageEssential),
            [PLUS]: translator(messages.AirRageStandardPlus),
        });

        data.push({
            id: 'personalLiability',
            order: 190,
            name: translator(messages.PersonalLiabilityName),
            [ESSENTIAL]: translator(messages.PersonalLiabilityEssential),
            [PLUS]: translator(messages.PersonalLiabilityStandardPlus),
        });

        data.push({
            id: 'legalExpenses',
            order: 200,
            name: translator(messages.LegalExpensesName),
            [ESSENTIAL]: translator(messages.LegalExpensesEssential),
            [PLUS]: translator(messages.LegalExpensesStandardPlus),
        });

        if (showAllCoverLevels) {
            data.push({
                id: 'cruiseMissedPort',
                order: hasCruiseTypeOfTrip ? 11 : 210,
                name: translator(messages.CruiseMissedPortName),
                [ESSENTIAL]: translator(messages.CruiseMissedPortEssential),
                [PLUS]: translator(messages.CruiseMissedPortStandardPlus),
            });

            data.push({
                id: 'cruiseRejoin',
                order: hasCruiseTypeOfTrip ? 12 : 220,
                name: translator(messages.CruiseRejoinName),
                [ESSENTIAL]: translator(messages.CruiseRejoinEssential),
                [PLUS]: translator(messages.CruiseRejoinStandardPlus, {
                    excessInput: excessValue
                }),
            });
        }

        return data;
    }, [modelVM, selectedTripVM, selectedTripIndex]);

    const dataRows = useMemo(() => {
        const data = tableData.sort((a, b) => {
            return a.order - b.order;
        });
        return data.map((row) => {
            return (
                <Fragment key={row.id}>
                    <tr key={row.id} id={`excess_row_${row.id}`}>
                        <td id={`title_excess_${row.id}`}>
                            {row.name}
                        </td>
                        {showEssentialCoverLevel && isSelectedTripSingle && (
                            <td id={`excess_${row.id}_${ESSENTIAL}`}>
                                {row[ESSENTIAL]}
                            </td>
                        )}
                        <td id={`excess_${row.id}_${PLUS}`}>
                            {row[PLUS]}
                        </td>
                    </tr>
                </Fragment>
            );
        });
    }, [tableData, showEssentialCoverLevel, hasCruiseTypeOfTrip]);

    const dataRowsMobile = useMemo(() => {
        const data = tableData.sort((a, b) => {
            return a.order - b.order;
        });
        return data.map((row) => {
            return (
                <Fragment key={row.id}>
                    <tr key={row.id} id={`excess_row_mobile_${row.id}`}>
                        <td colSpan={2} id={`title_excess_${row.id}`}>
                            {row.name}
                        </td>
                    </tr>
                    <tr>
                        {showEssentialCoverLevel && isSelectedTripSingle && (
                            <td id={`excess_${row.id}_${ESSENTIAL}`}>
                                {row[ESSENTIAL]}
                            </td>
                        )}
                        <td id={`excess_${row.id}_${PLUS}`}>
                            {row[PLUS]}
                        </td>
                    </tr>
                </Fragment>
            );
        });
    }, [tableData, showEssentialCoverLevel, hasCruiseTypeOfTrip]);

    const getModalJsx = useMemo(() => {
        return (
            <>
                <div className="sgExcessTableContainer">
                    <table className="sgExcessTable" id="excessTable">
                        <thead>
                            <tr>
                                {!isMobile && (
                                    <th />
                                )}
                                {showEssentialCoverLevel && isSelectedTripSingle && (
                                    <th>
                                        <div className="sgExcessTableHeader">
                                            <span className="sgExcessTableHeaderName">
                                                {translator(messages.EssentialName)}
                                            </span>
                                            <span className="sgExcessTableHeaderPer">
                                                {translator(messages.PerClaim)}
                                            </span>
                                        </div>
                                    </th>
                                )}
                                <th>
                                    <div className="sgExcessTableHeader">
                                        <span className="sgExcessTableHeaderName">
                                            {translator(showAllCoverLevels ? messages.StandardPlusName : messages.Standard)}
                                        </span>
                                        <span className="sgExcessTableHeaderPer">
                                            {translator(messages.PerClaim)}
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isMobile && (
                                <>
                                    {dataRowsMobile}
                                </>
                            )}
                            {!isMobile && (
                                <>
                                    {dataRows}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
                { showAllCoverLevels && (
                    <p className="sgModalTableCaveat">{translator(messages.CruiseBenefits)}</p>
                )}
            </>
        );
    }, []);

    const modalProps = {
        title: 'Excesses',
        titleVisibility: true,
        className: 'sgLargeModal sgTableOnlyModal',
        closeBtnLabel: messages.ModalMessageClose,
        isOpen,
        onResolve,
        modalJsx: getModalJsx
    };

    return <ModalCtrl {...modalProps} />;
}

export default SgSptExcessesModal;
